// frontend/src/pages/RegisterPage.js
// ----------------------------------------------------
// Modern Registration Page with Glassmorphism Design
// ----------------------------------------------------

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function RegisterPage() {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const { register, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    // Redirect if already authenticated
    React.useEffect(() => {
        if (isAuthenticated) {
            navigate("/live", { replace: true });
        }
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);

        // Validation
        if (!name || !email || !password) {
            setError("Please fill in all fields");
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            setLoading(false);
            return;
        }

        if (password.length < 6) {
            setError("Password must be at least 6 characters");
            setLoading(false);
            return;
        }

        const result = await register(name, email, password);

        if (result.success) {
            setSuccess(true);
        } else {
            setError(result.error);
        }

        setLoading(false);
    };

    return (
        <div className="auth-page">
            {/* Animated background */}
            <div className="auth-bg">
                <div className="auth-bg-gradient"></div>
                <div className="auth-bg-grid"></div>
            </div>

            {/* Register card */}
            <div className="auth-card">
                <div className="auth-header">
                    <div className="auth-logo">
                        <img src="/wrzzan-logo.png" alt="WRZZaN" style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8 }} />
                        <h1>WRZZaN</h1>
                    </div>
                    <p className="auth-subtitle">
                        {success ? "Registration Submitted" : "Create Your Account"}
                    </p>
                </div>

                {success ? (
                    <div className="auth-success">
                        <div className="success-icon">✅</div>
                        <h3>Registration Successful!</h3>
                        <p>Your account has been created. Please wait for admin approval before you can login.</p>
                        <Link to="/login" className="auth-btn" style={{ textDecoration: 'none', display: 'inline-flex', marginTop: '16px' }}>
                            <span>🔑</span> Go to Login
                        </Link>
                    </div>
                ) : (
                    <>
                        <form onSubmit={handleSubmit} className="auth-form">
                            <div className="form-group">
                                <label htmlFor="name">Full Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Enter your name"
                                    autoComplete="name"
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    autoComplete="email"
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Create a password"
                                    autoComplete="new-password"
                                    disabled={loading}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm your password"
                                    autoComplete="new-password"
                                    disabled={loading}
                                />
                            </div>

                            {error && (
                                <div className="auth-error">
                                    <span>⚠️</span> {error}
                                </div>
                            )}

                            <button type="submit" className="auth-btn" disabled={loading}>
                                {loading ? (
                                    <>
                                        <span className="btn-spinner"></span>
                                        Creating account...
                                    </>
                                ) : (
                                    <>
                                        <span>✨</span>
                                        Create Account
                                    </>
                                )}
                            </button>
                        </form>

                        <div className="auth-footer">
                            <p>
                                Already have an account?{" "}
                                <Link to="/login" className="auth-link">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* Decorative elements */}
            <div className="auth-decoration">
                <div className="decoration-circle circle-1"></div>
                <div className="decoration-circle circle-2"></div>
                <div className="decoration-circle circle-3"></div>
            </div>

            <style>{`
        .auth-success {
          text-align: center;
          padding: 20px;
        }
        .auth-success .success-icon {
          font-size: 3rem;
          margin-bottom: 16px;
        }
        .auth-success h3 {
          color: var(--color-success);
          margin-bottom: 12px;
        }
        .auth-success p {
          color: var(--color-text-secondary);
          line-height: 1.6;
        }
      `}</style>
        </div>
    );
}
